/**
 * 91. Approval Matrix Designer — thresholds; approver roles; delegation;
 * escalation. Reads the active matrix definition and renders its steps.
 *
 * Arabic and English (C7): every word from the catalogs; the record type the
 * matrix applies to and a system role are named in the interface's language;
 * rule expressions are code and are shown as written, left to right.
 * GET …/current names a matrix that does not exist yet (empty id) "New
 * approval matrix" in English; that name is the catalog's.
 */
import { useId } from "react";
import { Card, PageHeader, Badge, Skeleton } from "@/components/ui";
import { PendingButton, PendingNote } from "@/components/PendingAction";
import { useApiQuery } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { recordTypeLabel, statusLabel, valueLabel } from "@/lib/i18n/records";
import { formatQuantity } from "@/lib/money";

interface ApprovalStep {
  type: "Role" | "User" | "CounterSignature";
  roleCode?: string;
  minimum?: number;
}

interface MatrixRule {
  exp: string;
  steps: ApprovalStep[];
}

interface ApprovalMatrix {
  id: string;
  name: string;
  status: "Draft" | "Active" | "Superseded";
  triggerConditions: { entityType: string; rules: MatrixRule[] };
  escalationRules: Array<{
    afterHours: number;
    toRoleCode: string;
    slaBreach: boolean;
  }>;
}

export function ApprovalMatrixDesignerPage() {
  const { t } = useTranslation();
  const pendingId = useId();
  const { data, isLoading } = useApiQuery<ApprovalMatrix>(
    ["governance", "approval-matrix"],
    "/governance/approval-matrices/current",
  );

  const role = (code: string | undefined) =>
    code ? valueLabel(t, "systemRole", code) : "—";

  return (
    <div>
      <PageHeader
        title={t("admin.approvalMatrix.title")}
        subtitle={t("admin.approvalMatrix.subtitle")}
        actions={
          <>
            <PendingButton reasonId={pendingId}>
              {t("admin.approvalMatrix.validate")}
            </PendingButton>
            <PendingButton variant="primary" reasonId={pendingId}>
              {t("admin.approvalMatrix.activate")}
            </PendingButton>
          </>
        }
      />
      <PendingNote id={pendingId} reason="approvalDesigner" slice="F12-W" />
      {isLoading || !data ? (
        <Skeleton rows={8} />
      ) : (
        <>
          <Card
            title={data.id ? data.name : t("admin.approvalMatrix.unnamed")}
            actions={
              <Badge tone={data.status === "Active" ? "success" : "default"}>
                {statusLabel(t, "ApprovalMatrix", data.status)}
              </Badge>
            }
          >
            <p className="atlas-muted">
              {t("admin.approvalMatrix.entity", {
                entity: data.triggerConditions.entityType
                  ? recordTypeLabel(t, data.triggerConditions.entityType)
                  : "—",
              })}
            </p>
            {data.triggerConditions.rules.map((rule, i) => (
              <div key={i} className="mb-3">
                <code className="atlas-mono" dir="ltr">
                  {rule.exp}
                </code>
                <ul style={{ margin: "6px 0 0", fontSize: 14 }}>
                  {rule.steps.map((step, j) => (
                    <li key={j}>
                      {step.type === "Role" &&
                        t("admin.approvalMatrix.roleStep", {
                          role: role(step.roleCode),
                        })}
                      {step.type === "CounterSignature" &&
                        t("admin.approvalMatrix.counterSignatures", {
                          count: step.minimum ?? 0,
                        })}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </Card>
          <Card title={t("admin.approvalMatrix.escalation")} className="mt-4">
            <table className="atlas-table">
              <thead>
                <tr>
                  <th scope="col">{t("admin.approvalMatrix.afterHours")}</th>
                  <th scope="col">{t("admin.approvalMatrix.escalateTo")}</th>
                  <th scope="col">{t("admin.approvalMatrix.slaBreach")}</th>
                </tr>
              </thead>
              <tbody>
                {data.escalationRules.map((esc, i) => (
                  <tr key={i}>
                    <td>{formatQuantity(esc.afterHours, 2)}</td>
                    <td>{role(esc.toRoleCode)}</td>
                    <td>
                      {esc.slaBreach ? (
                        <Badge tone="warning">{t("shared.yes")}</Badge>
                      ) : (
                        t("shared.no")
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}
